import { ThirdPartyRefundProvider } from "@redotech/cockroachdb-codegen/congodb/third-party-refund-provider";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import {
  CustomerRefundProvider,
  isRefundDestinationProvider,
  normalizedOrderContactValue,
  OrderContact,
  recipientFieldForProvider,
  RecipientOrderComparison,
  RefundRecipientError,
  RefundRecipientField,
  RefundRecipientInput,
  ResolvedRefundRecipient,
  resolveRefundRecipient,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { useMemo, useState } from "react";

export interface RefundDestinationFormInput {
  /** Rails to offer, already filtered for entitlement and currency. */
  providers: CustomerRefundProvider[];
  /** The saved rail, or null for the original payment method. */
  persistedProvider: CustomerRefundProvider | null;
  persistedRecipient: RefundRecipientInput | null | undefined;
  /** Undefined until the order loads; drives the prefill and the mismatch check. */
  orderContact: OrderContact | undefined;
  /** Whether refunding to the original payment method is a real option. */
  originalPaymentAvailable: boolean;
  /** A Stripe bank account is onboarded off-page, so its readiness is told to the form rather than typed into it. */
  bankAccountReady?: boolean;
}

export interface RefundDestinationForm {
  providers: CustomerRefundProvider[];
  selectedProvider: CustomerRefundProvider | null;
  selectProvider: (provider: CustomerRefundProvider | null) => void;
  recipientField: RefundRecipientField | undefined;
  recipientValue: string;
  setRecipientValue: (value: string) => void;
  /** Call on blur; errors stay hidden until the customer has had a go at the field. */
  markTouched: () => void;
  resolved: ResolvedRefundRecipient | undefined;
  /** The pending edit differs from what is saved, so it is worth persisting. */
  isDirty: boolean;
  /** A rail is selected but its recipient isn't usable yet. */
  isIncomplete: boolean;
  validationMessage: string | undefined;
  orderMismatchWarning: string | undefined;
  /** Drops the pending edit and follows the persisted value again. */
  reset: () => void;
}

/**
 * Selection and validation for a refund destination, with no opinion on where
 * the value is persisted. The draft-return picker autosaves through it; the
 * post-submission editor saves on a button. Keeping the rules here is what
 * stops the two surfaces from validating differently.
 */
export function useRefundDestinationForm({
  providers,
  persistedProvider,
  persistedRecipient,
  orderContact,
  originalPaymentAvailable,
  bankAccountReady = false,
}: RefundDestinationFormInput): RefundDestinationForm {
  const { t } = useTranslation();

  /** undefined means "follow what is persisted", which may still be loading. */
  const [providerOverride, setProviderOverride] = useState<
    CustomerRefundProvider | null | undefined
  >(undefined);
  const [recipientTouched, setRecipientTouched] = useState(false);
  const [recipientDraft, setRecipientDraft] = useState<string | null>(null);

  const requestedProvider =
    providerOverride === undefined ? persistedProvider : providerOverride;
  /** A rail the team lost falls back. */
  const selectedProvider =
    requestedProvider && providers.includes(requestedProvider)
      ? requestedProvider
      : // Falling back to the original payment method would be a refund we
        // cannot issue, so prefer a rail that can actually pay out.
        originalPaymentAvailable
        ? null
        : (providers[0] ?? null);

  const recipientField = isRefundDestinationProvider(selectedProvider)
    ? recipientFieldForProvider(selectedProvider)
    : undefined;

  const persistedValue =
    recipientField === undefined
      ? ""
      : (recipientValueFor(recipientField, persistedRecipient) ?? "");

  /** Saves the customer retyping what the order already knows. */
  const prefillValue =
    recipientField === undefined || !orderContact
      ? undefined
      : normalizedOrderContactValue(recipientField, orderContact);

  const recipientValue =
    recipientDraft ?? (persistedValue || prefillValue) ?? "";

  const resolved = useMemo(() => {
    if (
      !isRefundDestinationProvider(selectedProvider) ||
      recipientField === undefined ||
      !orderContact
    ) {
      return undefined;
    }
    return resolveRefundRecipient(
      selectedProvider,
      recipientField === RefundRecipientField.PHONE
        ? { phone: recipientValue }
        : { email: recipientValue },
      orderContact,
    );
  }, [selectedProvider, recipientField, recipientValue, orderContact]);

  const errorMessages: Record<RefundRecipientError, string> = {
    [RefundRecipientError.MISSING]:
      recipientField === RefundRecipientField.PHONE
        ? t("Enter the mobile number on your Venmo account")
        : t("Enter the email address on your PayPal account"),
    [RefundRecipientError.INVALID_EMAIL]: t("Enter a valid email address"),
    [RefundRecipientError.INVALID_US_PHONE]: t(
      "Enter a valid US mobile number",
    ),
  };

  const reset = useHandler(() => {
    setRecipientDraft(null);
    setProviderOverride(undefined);
    setRecipientTouched(false);
  });

  const selectProvider = useHandler(
    (provider: CustomerRefundProvider | null) => {
      setProviderOverride(provider);
      setRecipientDraft(null);
      setRecipientTouched(false);
    },
  );

  const setRecipientValue = useHandler((value: string) => {
    setRecipientDraft(value);
  });

  const markTouched = useHandler(() => setRecipientTouched(true));

  const bankAccountSelected =
    selectedProvider === ThirdPartyRefundProvider.Stripe;

  // A bank account is persisted by the onboarding round-trip, never by this form.
  const isDirty =
    !bankAccountSelected && resolved?.ok
      ? resolved.normalized !== persistedValue ||
        persistedProvider !== selectedProvider
      : false;

  const showError = recipientTouched && resolved && !resolved.ok;

  return {
    providers,
    selectedProvider,
    selectProvider,
    recipientField,
    recipientValue,
    setRecipientValue,
    markTouched,
    resolved,
    isDirty,
    isIncomplete: bankAccountSelected
      ? !bankAccountReady
      : !!selectedProvider && !resolved?.ok,
    validationMessage: showError ? errorMessages[resolved.error] : undefined,
    orderMismatchWarning:
      resolved?.ok &&
      resolved.comparison === RecipientOrderComparison.MISMATCHED
        ? recipientField === RefundRecipientField.PHONE
          ? t("This number doesn't match the one on your order")
          : t("This email doesn't match the one on your order")
        : undefined,
    reset,
  };
}

function recipientValueFor(
  field: RefundRecipientField,
  recipient: RefundRecipientInput | null | undefined,
): string | undefined {
  const value =
    field === RefundRecipientField.PHONE ? recipient?.phone : recipient?.email;
  return value ?? undefined;
}
